import { useReloadOnStaleVersion, useVersionCheckOnNavigate } from './revalidation';
import React from 'react';
import './css/index.css';
import { Assets, AssetWrapper, AssetHistoryDetail } from './components/Assets';
import { HomePage } from './components/homepage';
import { Breadcrumbs, Header } from './components/Header';
import { Members, MemberWrapper, MemberHistoryDetail } from './components/members';
import { DeviceSubTypes, DeviceSubTypeDetails } from "./components/DeviceSubTypes";
import { Readiness, ReadinessMenu } from './components/Readiness';
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { TeamHistoryDetail, Teams, TeamWrapper } from './components/teams';
import { NavigateWithId } from './components/common/NavigateWithId';
import { Onestop } from './components/Onestop/Onestop';
import { createContext } from 'react';
import {
    AuthProvider,
    ChangePassword,
    Login,
    PasswordReset,
    PasswordResetRequest,
} from './components/auth';
import { AuthorizedRoute, ProtectedRoute } from "./ProtectedRoute";

import "semantic-ui-css/semantic.min.css";
import "./css/App.css";
import { Checklist } from './components/DeviceSubTypes/Checklist';
import { useState } from 'react';
import { OnestopOverlay } from './components/Onestop/OnestopOverlay/OnestopOverlay';
import { AssetProblemIntakeForm } from './components/Assets/AssetProblemIntakeForm';
import { EventDetail, Events } from './components/Events';
import { AssetInspectionForm, AssetInspections } from "./components/AssetInspection";
import { MaintenancePage } from "./components/Maintenance";
import { MaintenanceDashboard } from "./components/Maintenance";
import { Forms } from './components/Forms';
import { UtilityStrike, UtilityStrikeFormDetails } from './components/Forms/UtilityStrike';
import { Questions, QuestionFormDetails } from './components/Forms/Questions';
import { JSAForms, JSAFormDetails } from './components/Forms/JSAForm';
import { RoadblockDashboard, RoadblockForm, RoadblockDetail, RoadblockTypeManager } from './components/Forms/Roadblock';
import { RoadblockReport } from './components/Roadblock';
import { FutureReadinessPage } from './components/FutureReadiness';
import { TeamCalendarView } from './components/TeamCalendar';
import { Documents, DocumentsHub } from './components/Documents';
import { VideoLibrary } from './components/VideoLibrary';
import { ContactsHub } from './components/Contacts';
import { SupportPage, TeamAssetRequirementManager } from './components/Support';
import { DFNList } from './components/DFN';
import { Markets } from './components/Markets';
import { TeamTypes } from './components/TeamTypes';
import { AssetMakesModels } from './components/AssetMakesModels';
import { FormChecklistList, FormChecklistDetail, FormChecklistForm } from './components/FormChecklists';
import { ManagerDashboard } from './components/ManagerDashboard';
import { AssetDashboard } from './components/AssetDashboard';
import { ReportingDashboard } from './components/ReportingDashboard';
import { AssetProblemReport } from './components/AssetProblemReport';

export const GlobalContext = createContext(
    {
        showOnestopOverlay: false,
        setShowOnestopOverlay: () => { }
    }
)

const BaseLayout = () => {
    const ctx = React.useContext(GlobalContext)
    useVersionCheckOnNavigate()
    return (
        <div className="App">
            <Header />
            <Breadcrumbs />
            <Outlet />
            {ctx.showOnestopOverlay && <OnestopOverlay />}
        </div>
    )
}

const routeConfig = [
        {
            path: "/",
            element: <ProtectedRoute><BaseLayout /></ProtectedRoute>,
            handle: {
                crumbInfo: {
                    to: '/',
                    text: "Home"
                }
            },
            children: [
                {
                    path: "",
                    index: true,
                    element: <HomePage />
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'reporting_dashboard',
                            to: 'reporting_dashboard',
                            text: 'Reporting Dashboard'
                        }
                    },
                    path: "/dashboard",
                    element: <AuthorizedRoute route='reporting_dashboard'><ReportingDashboard /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'onestop',
                            to: 'onestop',
                            text: 'Onestop'
                        }
                    },
                    path: "/onestop",
                    element: <AuthorizedRoute route='onestop'><Onestop /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'readiness',
                            to: 'readiness',
                            text: 'Readiness'
                        }
                    },
                    path: "/readiness",
                    element: <AuthorizedRoute route='readiness'><Readiness /></AuthorizedRoute>
                },
                {
                    path: "/readinessreport",
                    handle: {
                        crumbInfo: {
                            text: 'Readiness Report'
                        }
                    },
                    children: [
                        {
                            path: "/readinessreport/team",
                            element: <AuthorizedRoute route='readinessReport'><ReadinessMenu type='team' /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    permission: 'readinessReport',
                                    to: 'readinessreport/team',
                                    text: 'Team',
                                    searchTitle: 'Team Readiness Report',
                                }
                            }
                        },
                        {
                            path: "/readinessreport/asset",
                            element: <AuthorizedRoute route='readinessReport'><ReadinessMenu type='asset' /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    permission: 'readinessReport',
                                    to: 'readinessreport/asset',
                                    text: 'Asset',
                                    searchTitle: 'Asset Readiness Report',
                                }
                            }
                        },
                        {
                            path: "/readinessreport/member",
                            element: <AuthorizedRoute route='readinessReport'><ReadinessMenu type='member' /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    permission: 'readinessReport',
                                    to: 'readinessreport/member',
                                    text: 'Member',
                                    searchTitle: 'Member Readiness Report',
                                }
                            }
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'maintenance',
                            to: '/maintenance',
                            text: 'Maintenance Dashboard'
                        }
                    },
                    path: "/maintenance",
                    element: <AuthorizedRoute route='maintenance'><MaintenanceDashboard /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'teams',
                            to: '/teams',
                            text: "Teams"
                        }
                    },
                    path: "/teams",
                    children: [
                        {
                            element: <AuthorizedRoute route='teams'><Teams /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/teams/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/teams/:id/view"
                                }
                            },
                            children: [
                                {
                                    path: "/teams/:id",
                                    element: <AuthorizedRoute route='teams'><NavigateWithId to={'/teams/:id/view'} /></AuthorizedRoute>,
                                },
                                {
                                    path: "/teams/:id/view",
                                    handle: {
                                        crumbInfo: {
                                            text: "Team Details",
                                            to: "/teams/:id/view",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='teams'><TeamWrapper chosenElement="View" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/teams/:id/changelog",
                                    handle: {
                                        crumbInfo: {
                                            text: "Team Details",
                                            to: "/teams/:id/changelog",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='teams'><TeamWrapper chosenElement="Changelog" /></AuthorizedRoute>,
                                            index: true
                                        },
                                        {
                                            path: "/teams/:id/changelog/:id2",
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id", "id2"],
                                                    replaceParam: "id2"
                                                }
                                            },
                                            element: <AuthorizedRoute route='teams'><TeamHistoryDetail /></AuthorizedRoute>
                                        }
                                    ]
                                },
                                {
                                    path: "/teams/:id/calendar",
                                    handle: {
                                        crumbInfo: {
                                            text: "Team Details",
                                            to: "/teams/:id/calendar",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='teams'><TeamWrapper chosenElement="Calendar" /></AuthorizedRoute>,
                                            index: true
                                        }
                                    ]

                                }
                            ]
                        }
                    ]
                },

                {
                    handle: {
                        crumbInfo: {
                            permission: 'members',
                            to: '/members',
                            text: "Members"
                        }
                    },
                    path: "/members",
                    children: [
                        {
                            element: <AuthorizedRoute route='members'><Members /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/members/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/members/:id/view"
                                }
                            },
                            children: [
                                {
                                    path: "/members/:id",
                                    element: <AuthorizedRoute route='members'><NavigateWithId to={'/members/:id/view'} /></AuthorizedRoute>,
                                },
                                {
                                    path: "/members/:id/view",
                                    handle: {
                                        crumbInfo: {
                                            text: "Member Details",
                                            to: "/members/:id/view",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='members'><MemberWrapper chosenElement="View" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/members/:id/schedule",
                                    handle: {
                                        crumbInfo: {
                                            text: "Member Details",
                                            to: "/members/:id/schedule",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='members'><MemberWrapper chosenElement="Schedule" /></AuthorizedRoute>,
                                            index: true
                                        }
                                    ]
                                },
                                {
                                    path: "/members/:id/changelog",
                                    handle: {
                                        crumbInfo: {
                                            text: "Member Details",
                                            to: "/members/:id/changelog",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='members'><MemberWrapper chosenElement="Changelog" /></AuthorizedRoute>,
                                            index: true
                                        },
                                        {
                                            path: "/members/:id/changelog/:id2",
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id", "id2"],
                                                    replaceParam: "id2"
                                                }
                                            },
                                            element: <AuthorizedRoute route='members'><MemberHistoryDetail /></AuthorizedRoute>
                                        }
                                    ]

                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'assets',
                            to: '/assets',
                            text: "Assets"
                        }
                    },
                    path: "/assets",
                    children: [
                        {
                            element: <AuthorizedRoute route='assets'><Assets /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/assets/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/assets/:id/view"
                                }
                            },
                            children: [
                                {
                                    path: "/assets/:id",
                                    element: <AuthorizedRoute route='assets'><NavigateWithId to={'/assets/:id/view'} /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/view",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/view",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="View" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/checklist",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/checklist",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Checklist" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/problems",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/problems",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Problems" /></AuthorizedRoute>,
                                            index: true
                                        },
                                        {
                                            path: "/assets/:id/problems/:id2",
                                            element: <AuthorizedRoute route='assets'><AssetProblemIntakeForm /></AuthorizedRoute>,
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id", "id2"],
                                                    replaceParam: "id2"
                                                }
                                            }
                                        }
                                    ]
                                },
                                {
                                    path: "/assets/:id/changelog",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/changelog",
                                            params: ["id"]
                                        }
                                    },
                                    children: [
                                        {
                                            element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Changelog" /></AuthorizedRoute>,
                                            index: true
                                        },
                                        {
                                            path: "/assets/:id/changelog/:id2",
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id", "id2"],
                                                    replaceParam: "id2"
                                                }
                                            },
                                            element: <AuthorizedRoute route='assets'><AssetHistoryDetail /></AuthorizedRoute>
                                        }
                                    ]

                                },
                                {
                                    path: "/assets/:id/lifecycle",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/lifecycle",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Lifecycle" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/images",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/images",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Images" /></AuthorizedRoute>,
                                },
                                {
                                    path: "/assets/:id/moves",
                                    handle: {
                                        crumbInfo: {
                                            text: "Asset Details",
                                            to: "/assets/:id/moves",
                                            params: ["id"]
                                        }
                                    },
                                    element: <AuthorizedRoute route='assets'><AssetWrapper chosenElement="Moves" /></AuthorizedRoute>,
                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'devicesubtypes',
                            to: '/devicesubtypes',
                            text: "Asset SubTypes"
                        }
                    },
                    path: "/devicesubtypes",
                    children: [
                        {
                            element: <AuthorizedRoute route='devicesubtypes'><DeviceSubTypes /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/devicesubtypes/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/devicesubtypes/:id"
                                }
                            },
                            children: [
                                {
                                    element: <AuthorizedRoute route='devicesubtypes'><DeviceSubTypeDetails /></AuthorizedRoute>,
                                    index: true,
                                },
                                {
                                    path: "/devicesubtypes/:id/checklist",
                                    element: <AuthorizedRoute route='devicesubtypes'><Checklist /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            to: '/',
                                            replacParam: "id",
                                            params: ["id"],
                                            text: "Checklist"
                                        }
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'events',
                            to: '/events',
                            text: "Events"
                        }
                    },
                    path: "/events",
                    children: [
                        {
                            element: <AuthorizedRoute route='events'><Events /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/events/:id",
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/events/:id"
                                }
                            },
                            element: <AuthorizedRoute route='events'><EventDetail /></AuthorizedRoute>
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'teamcalendar',
                            to: '/teamcalendar',
                            text: "Team Calendar"
                        }
                    },
                    path: "/teamcalendar",
                    element: <AuthorizedRoute route='teamcalendar'><TeamCalendarView /></AuthorizedRoute>
                },
                {
                    path: "/password-change",
                    element: <ChangePassword />
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'standdown',
                            to: '/standdown',
                            text: 'Stand-Down'
                        }
                    },
                    path: "/standdown",
                    children: [
                        {
                            element: <AuthorizedRoute route='standdown'><AssetInspections /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "/standdown/:id",
                            element: <AuthorizedRoute route='standdown'><AssetInspectionForm mode={"edit"} /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    params: ["id"],
                                    replaceParam: "id",
                                    to: "/standdown/:id/"
                                }
                            }
                        },
                        {
                            path: "/standdown/new",
                            element: <AuthorizedRoute route='standdown'><AssetInspectionForm mode={"create"} /></AuthorizedRoute>,

                        }
                    ]

                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'forms',
                            to: '/forms',
                            text: "Forms"
                        }
                    },
                    path: "/forms",
                    children: [
                        {
                            element: <AuthorizedRoute route='forms'><Forms /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            path: "utilitystrike",
                            children: [
                                {
                                    element: <AuthorizedRoute route='forms'><UtilityStrike /></AuthorizedRoute>,
                                    index: true,
                                    handle: {
                                        crumbInfo: {
                                            permission: 'forms',
                                            to: '/forms/utilitystrike',
                                            text: 'Utility Strike'
                                        }
                                    }
                                },
                                {
                                    path: "new",
                                    element: <AuthorizedRoute route='forms'><UtilityStrikeFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            permission: 'forms',
                                            to: '/forms/utilitystrike/new',
                                            text: 'New Utility Strike Form'
                                        }
                                    }
                                },
                                {
                                    path: ":id",
                                    element: <AuthorizedRoute route='forms'><UtilityStrikeFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            params: ["id"],
                                            replaceParam: "id",
                                            to: "/forms/utilitystrike/:id",
                                            text: 'Utility Strike Form Details'
                                        }
                                    }
                                }
                            ]
                        },
                        {
                            path: "jsaform",
                            handle: {
                                crumbInfo: {
                                    permission: 'forms',
                                    to: '/forms/jsaform',
                                    text: "JSA Forms"
                                }
                            },
                            children: [
                                {
                                    element: <AuthorizedRoute route='forms'><JSAForms /></AuthorizedRoute>,
                                    index: true
                                },
                                {
                                    path: "new",
                                    element: <AuthorizedRoute route='forms'><JSAFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            permission: 'forms',
                                            to: '/forms/jsaform/new',
                                            text: 'New JSA Form'
                                        }
                                    }
                                },
                                {
                                    path: ":id",
                                    element: <AuthorizedRoute route='forms'><JSAFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            params: ["id"],
                                            replaceParam: "id",
                                            to: "/forms/jsaform/:id",
                                            text: 'JSA Form Details'
                                        }
                                    }
                                }
                            ]
                        },
                        {
                            path: 'questions',
                            handle: {
                                crumbInfo: {
                                    permission: 'forms',
                                    to: '/forms/questions',
                                    text: 'Questions Form',
                                },
                            },
                            children: [
                                {
                                    index: true,
                                    element: <AuthorizedRoute route='forms'><Questions /></AuthorizedRoute>,
                                },
                                {
                                    path: 'new',
                                    element: <AuthorizedRoute route='forms'><QuestionFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            permission: 'forms',
                                            to: '/forms/questions/new',
                                            text: 'New Question Form',
                                        },
                                    },
                                },
                                {
                                    path: ':id',
                                    element: <AuthorizedRoute route='forms'><QuestionFormDetails /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            params: ['id'],
                                            replaceParam: 'id',
                                            to: '/forms/questions/:id',
                                            text: 'Question Form Details',
                                        },
                                    },
                                },
                            ],
                        },
                        {
                            path: "roadblock",
                            children: [
                                {
                                    index: true,
                                    element: <AuthorizedRoute route='forms'><RoadblockDashboard /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            permission: 'forms',
                                            to: '/forms/roadblock',
                                            text: 'Roadblocks'
                                        }
                                    }
                                },
                                {
                                    path: "new",
                                    element: <AuthorizedRoute route='forms'><RoadblockForm /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            permission: 'forms',
                                            to: '/forms/roadblock/new',
                                            text: 'New Roadblock Form'
                                        }
                                    }
                                },
                                {
                                    path: ":id",
                                    element: <AuthorizedRoute route='forms'><RoadblockDetail /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            params: ['id'],
                                            replaceParam: 'id',
                                            to: '/forms/roadblock/:id',
                                            text: 'Roadblock Detail'
                                        }
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'roadblock_report',
                            to: '/roadblock/report',
                            text: 'Roadblocks Report'
                        }
                    },
                    path: "/roadblock/report",
                    element: <AuthorizedRoute route='roadblock_report'><RoadblockReport /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'future_readiness',
                            to: '/readiness/planning',
                            text: 'Future Readiness Planning'
                        }
                    },
                    path: "/readiness/planning",
                    element: <AuthorizedRoute route='future_readiness'><FutureReadinessPage /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'forms',
                            to: '/documents',
                            text: 'Documents'
                        }
                    },
                    path: "/documents",
                    children: [
                        {
                            index: true,
                            element: <AuthorizedRoute route='forms'><DocumentsHub /></AuthorizedRoute>
                        },
                        {
                            path: 'list',
                            element: <AuthorizedRoute route='forms'><Documents /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    permission: 'forms',
                                    to: '/documents/list',
                                    text: 'Documents'
                                }
                            }
                        },
                        {
                            path: 'video-content',
                            element: <AuthorizedRoute route='forms'><VideoLibrary /></AuthorizedRoute>,
                            handle: {
                                crumbInfo: {
                                    permission: 'forms',
                                    to: '/documents/video-content',
                                    text: 'Video Content'
                                }
                            }
                        },
                        {
                            path: "form-checklists",
                            handle: {
                                crumbInfo: {
                                    permission: 'documents',
                                    to: '/documents/form-checklists',
                                    text: 'Form Checklists'
                                }
                            },
                            children: [
                                { index: true, element: <AuthorizedRoute route='documents'><FormChecklistList /></AuthorizedRoute> },
                                {
                                    path: "new",
                                    element: <AuthorizedRoute route='documents'><FormChecklistForm /></AuthorizedRoute>,
                                    handle: {
                                        crumbInfo: {
                                            permission: 'documents',
                                            to: '/documents/form-checklists/new',
                                            text: 'New Form Checklist'
                                        }
                                    }
                                },
                                {
                                    path: ":id",
                                    children: [
                                        {
                                            index: true,
                                            element: <AuthorizedRoute route='documents'><FormChecklistDetail /></AuthorizedRoute>,
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id"],
                                                    replaceParam: "id",
                                                    to: "/documents/form-checklists/:id",
                                                    text: 'Form Checklist Detail'
                                                }
                                            }
                                        },
                                        {
                                            path: "edit",
                                            handle: {
                                                crumbInfo: {
                                                    params: ["id"],
                                                    replaceParam: "id",
                                                    to: '/documents/form-checklists/:id/edit',
                                                    text: 'Edit Form Checklist'
                                                }
                                            },
                                            element: <AuthorizedRoute route='documents'><FormChecklistForm /></AuthorizedRoute>
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/contacts',
                            text: 'Contacts Hub'
                        }
                    },
                    path: "/contacts",
                    element: <ProtectedRoute><ContactsHub /></ProtectedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'manager_dashboard',
                            to: '/manager/dashboard',
                            text: 'Manager Dashboard'
                        }
                    },
                    path: "/manager/dashboard",
                    element: <AuthorizedRoute route='manager_dashboard'><ManagerDashboard /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'asset_dashboard',
                            to: '/assets/dashboard',
                            text: 'Asset Dashboard'
                        }
                    },
                    path: "/assets/dashboard",
                    element: <AuthorizedRoute route='asset_dashboard'><AssetDashboard /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            to: '/manager/asset-problem-report',
                            text: 'Asset Problem Report',
                            permission: 'asset_problem_report'
                        }
                    },
                    path: "/manager/asset-problem-report",
                    element: <AuthorizedRoute route='asset_problem_report'><AssetProblemReport /></AuthorizedRoute>
                },
                {
                    handle: {
                        crumbInfo: {
                            permission: 'support',
                            to: '/support',
                            text: 'Support'
                        }
                    },
                    path: "/support",
                    children: [
                        {
                            element: <AuthorizedRoute route='support'><SupportPage /></AuthorizedRoute>,
                            index: true
                        },
                        {
                            handle: {
                                crumbInfo: {
                                    permission: 'support',
                                    to: '/support/dfn',
                                    text: 'DFN'
                                }
                            },
                            path: "/support/dfn",
                            element: <AuthorizedRoute route='support'><DFNList /></AuthorizedRoute>
                        },
                        {
                            handle: {
                                crumbInfo: {
                                    permission: 'support',
                                    to: '/support/roadblock-types',
                                    text: 'Roadblock Types'
                                }
                            },
                            path: "/support/roadblock-types",
                            element: <AuthorizedRoute route='support'><RoadblockTypeManager /></AuthorizedRoute>
                        },
                        {
                            handle: {
                                crumbInfo: {
                                    to: '/support/team-asset-requirements',
                                    text: 'Team Asset Requirements'
                                }
                            },
                            path: "/support/team-asset-requirements",
                            element: <ProtectedRoute><TeamAssetRequirementManager /></ProtectedRoute>
                        },
                        {
                            handle: {
                                crumbInfo: {
                                    permission: 'support',
                                    to: '/support/markets',
                                    text: 'Markets'
                                }
                            },
                            path: "/support/markets",
                            element: <AuthorizedRoute route='support'><Markets /></AuthorizedRoute>
                        },
                        {
                            handle: {
                                crumbInfo: {
                                    to: '/support/team-types',
                                    text: 'Team Types'
                                }
                            },
                            path: "/support/team-types",
                            element: <AuthorizedRoute route='support'><TeamTypes /></AuthorizedRoute>
                        },
                        {
                            handle: {
                                crumbInfo: {
                                    permission: 'support',
                                    to: '/support/asset-makes-models',
                                    text: 'Asset Makes & Models'
                                }
                            },
                            path: "/support/asset-makes-models",
                            element: <AuthorizedRoute route='support'><AssetMakesModels /></AuthorizedRoute>
                        }
                    ]
                }
            ]
        },
        {
            path: "/login",
            element: <Login />
        },
        {
            path: "/password-reset",
            children: [
                {
                    element: <PasswordResetRequest />,
                    index: true
                },
                {
                    path: "/password-reset/:token",
                    element: <PasswordReset />,
                }
            ]
        }
    ];

const flattenCrumbRoutes = (nodes) => {
    const out = [];
    const walk = (arr, trail) => {
        for (const r of arr || []) {
            const info = r.handle?.crumbInfo;
            let nextTrail = trail;
            if (info && info.text) {
                nextTrail = [...trail, info.text];
                if (info.to && !info.replaceParam) {
                    out.push({
                        title: info.searchTitle || info.text,
                        path: info.to,
                        trail: nextTrail,
                        tags: info.tags || [],
                        permission: info.permission || null,
                        hasParams: Boolean(info.params && info.params.length)
                    });
                }
            }
            if (r.children) walk(r.children, nextTrail);
        }
    };
    walk(nodes, []);
    return out;
};

export const ROUTE_CATALOG = (() => {
    const flat = flattenCrumbRoutes(routeConfig);
    const normalizePath = (p) => (p.startsWith('/') ? p : '/' + p);
    const seen = new Set();
    const out = [];
    for (const r of flat) {
        const path = normalizePath(r.path);
        const key = r.title + '|' + path;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ ...r, path });
    }
    return out;
})();

export const App = () => {
    const [showOnestopOverlay, setShowOnestopOverlay] = useState(false)
    useReloadOnStaleVersion()
    const globalContextValue = { showOnestopOverlay, setShowOnestopOverlay }
    const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";
    const router = createBrowserRouter(routeConfig);

    return (
        <AuthProvider>
            <GlobalContext.Provider value={globalContextValue}>
                {isMaintenanceMode ? <MaintenancePage /> : <RouterProvider router={router} />}
            </GlobalContext.Provider>
        </AuthProvider>
    )

}
